/**
 * useGoogleReviews
 * Busca avaliações 5 estrelas diretamente do Google Places API (Maps JS API).
 * Não tem problema de CORS: o SDK do Maps lida com isso automaticamente.
 *
 * Requisito: VITE_GOOGLE_MAPS_KEY configurado nas variáveis de ambiente.
 * Place ID: armazenado em home_sections (chave: google_config).
 */

import { useEffect, useState } from "react";

declare global {
  interface Window {
    google: any;
    _googleMapsLoading?: Promise<void>;
  }
}

export interface GoogleReview {
  text: string;
  name: string;
  time: string;
  rating: number;
  photoUrl?: string;
}

function loadMapsSDK(apiKey: string): Promise<void> {
  if (window.google?.maps?.places) return Promise.resolve();
  if (window._googleMapsLoading) return window._googleMapsLoading;

  window._googleMapsLoading = new Promise((resolve, reject) => {
    const cb = "_gmInit_" + Date.now();
    (window as any)[cb] = () => { resolve(); delete (window as any)[cb]; };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${cb}`;
    s.async = true;
    s.onerror = () => reject(new Error("Falha ao carregar Google Maps SDK"));
    document.head.appendChild(s);
  });
  return window._googleMapsLoading;
}

async function fetchReviews(placeId: string, apiKey: string, minRating: number): Promise<{ reviews: GoogleReview[]; rating: number | null }> {
  await loadMapsSDK(apiKey);

  return new Promise((resolve) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const svc = new window.google.maps.places.PlacesService(div);

    svc.getDetails(
      { placeId, fields: ["reviews", "rating"] },
      (place: any, status: string) => {
        document.body.removeChild(div);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.reviews) {
          resolve({ reviews: [], rating: null });
          return;
        }
        const filtered: GoogleReview[] = (place.reviews as any[])
          .filter((r) => r.rating >= minRating)
          .sort((a, b) => b.time - a.time)
          .slice(0, 6)
          .map((r) => ({
            text: r.text,
            name: r.author_name,
            time: r.relative_time_description,
            rating: r.rating,
            photoUrl: r.profile_photo_url,
          }));
        resolve({ reviews: filtered, rating: place.rating ?? null });
      }
    );
  });
}

interface UseGoogleReviewsResult {
  reviews: GoogleReview[];
  rating: number | null;
  loading: boolean;
  error: string | null;
  configured: boolean;
}

export function useGoogleReviews(
  placeId?: string,
  minRating = 5
): UseGoogleReviewsResult {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  const configured = !!(apiKey && placeId);

  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [rating, setRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(configured);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;

    setLoading(true);
    fetchReviews(placeId!, apiKey!, minRating)
      .then(({ reviews: r, rating: rt }) => {
        if (cancelled) return;
        setReviews(r);
        setRating(rt);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [placeId, apiKey, minRating, configured]);

  return { reviews, rating, loading, error, configured };
}
